import React, { useEffect } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Space,
  Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { CODE_PREFIX_PATTERN, FIELD_LIMITS } from './constants';
import { useCreateCreator } from './hooks/useCreatorMutations';
import type { CreateCreatorPayload } from './types';

const { Text } = Typography;

interface PayoutMethodPair {
  key?: string;
  value?: string;
}

interface CreatorFormValues {
  name: string;
  handle: string;
  email: string;
  password: string;
  phone?: string;
  codePrefix: string;
  payoutMethod?: PayoutMethodPair[];
}

interface CreatorCreateDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * §2.1 — create a creator.
 *
 * Validation mirrors the documented server-side limits so the form fails fast rather
 * than round-tripping a 400. Duplicate email / taken code prefix can only be detected
 * server-side, so those arrive as a notification from the mutation hook.
 *
 * `payoutMethod` is documented as a free-form object that no payout logic reads, so
 * it's edited as key/value pairs rather than fixed fields — that keeps it genuinely
 * free-form without inventing a schema the API never specified.
 */
const CreatorCreateDrawer: React.FC<CreatorCreateDrawerProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<CreatorFormValues>();
  const { isDarkMode } = useTheme();
  const { mutate: createCreator, isPending } = useCreateCreator();

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = (values: CreatorFormValues) => {
    const payoutMethod = (values.payoutMethod || [])
      .filter((pair) => pair?.key?.trim())
      .reduce<Record<string, unknown>>((acc, pair) => {
        acc[pair.key!.trim()] = pair.value ?? '';
        return acc;
      }, {});

    const payload: CreateCreatorPayload = {
      name: values.name.trim(),
      handle: values.handle.trim(),
      email: values.email.trim(),
      password: values.password,
      codePrefix: values.codePrefix.trim(),
    };

    if (values.phone?.trim()) payload.phone = values.phone.trim();
    if (Object.keys(payoutMethod).length > 0) payload.payoutMethod = payoutMethod;

    createCreator(payload, {
      onSuccess: () => {
        form.resetFields();
        onClose();
      },
    });
  };

  return (
    <Drawer
      title="Add Creator"
      placement="right"
      onClose={onClose}
      open={open}
      size={600}
      destroyOnClose
      styles={{
        body: { background: isDarkMode ? '#1a202c' : '#ffffff' },
        header: {
          background: isDarkMode ? '#1a202c' : '#ffffff',
          borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
        },
      }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="primary" loading={isPending} onClick={() => form.submit()}>
            Create Creator
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isPending}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter a name' },
            { max: FIELD_LIMITS.NAME_MAX, message: `At most ${FIELD_LIMITS.NAME_MAX} characters` },
          ]}
        >
          <Input placeholder="Jane Doe" />
        </Form.Item>

        <Form.Item
          label="Handle"
          name="handle"
          extra="Display-only social handle. It is never validated against a real account."
          rules={[
            { required: true, message: 'Please enter a handle' },
            {
              max: FIELD_LIMITS.HANDLE_MAX,
              message: `At most ${FIELD_LIMITS.HANDLE_MAX} characters`,
            },
          ]}
        >
          <Input placeholder="@echo.eats" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          extra="Must be unique across all creators."
          rules={[
            { required: true, message: 'Please enter an email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="jane@example.com" />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          extra="The creator's login password for their own portal."
          rules={[
            { required: true, message: 'Please enter a password' },
            {
              min: FIELD_LIMITS.PASSWORD_MIN,
              message: `At least ${FIELD_LIMITS.PASSWORD_MIN} characters`,
            },
          ]}
        >
          <Input.Password placeholder="At least 8 characters" autoComplete="new-password" />
        </Form.Item>

        <Form.Item label="Phone" name="phone">
          <Input placeholder="Optional" />
        </Form.Item>

        <Form.Item
          label="Code Prefix"
          name="codePrefix"
          extra="Every code this creator creates must start with this, uppercased. Must be unique within your merchant account."
          rules={[
            { required: true, message: 'Please enter a code prefix' },
            {
              max: FIELD_LIMITS.CODE_PREFIX_MAX,
              message: `At most ${FIELD_LIMITS.CODE_PREFIX_MAX} characters`,
            },
            {
              pattern: CODE_PREFIX_PATTERN,
              message: 'Letters and numbers only',
            },
          ]}
        >
          <Input placeholder="ECHO" />
        </Form.Item>

        <Form.Item label="Payout Details">
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Optional and free-form — how you plan to actually pay them (Venmo handle, bank
            details). Purely informational; no payout logic reads it.
          </Text>
          <Form.List name="payoutMethod">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item {...rest} name={[name, 'key']} noStyle>
                      <Input placeholder="Label (e.g. venmo)" style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'value']} noStyle>
                      <Input placeholder="Value" style={{ width: 250 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add payout detail
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreatorCreateDrawer;
